/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo } from "react";
import { useIntl } from "react-intl";
import {useAppSelector} from "hooks/useAppSelector";
import { VIP } from "app/types/common";

const fillProgress = (subLevels: {
    xp: {
        current: number;
        max: number;
    }
}[]) => {
    const array = Array.from({
        length: subLevels.length
    });

    return array.map((item, i) => ({
        value: 100 / subLevels?.[i]?.xp?.max * subLevels?.[i]?.xp?.current
    }));
}

export const useVipLevelsData = () => {
    const level = useAppSelector((state) => state.user.user?.level) || 0;
    const exp = useAppSelector((state) => state.user.user?.exp) || 0;
    const vipList = useAppSelector((state) => state.config.platformConfig?.vipList || []);
    const {$t} = useIntl();

    const getSubLevels = (rangeVipList: VIP[], nextVilLevel: VIP[]) => {
        const res = rangeVipList?.map((v, i, arr) => {
            const matchName = v.name?.match(/^([^\d]+)\s*(\d+)$/);
            return {
                name: matchName
                    ? $t({ id: String(matchName[1].trim() || '') }) + ' ' + matchName[2]
                    : $t({ id: v.name }) || '',
                isCurrent: level === v.level,
                isComplete: exp >= v.exp,
                isInactive: exp < v.exp,
                xp: {
                    current: level < v.level ? 0 : level > v.level ? (arr[i + 1]?.exp || nextVilLevel[0]?.exp) : exp,
                    max: arr[i + 1]?.exp || nextVilLevel[0]?.exp || arr[i]?.exp || 0,
                },
            };
        });
        return res || [];
    };

    const bronzeData = useMemo(() => {
        const rangeVipList = vipList?.filter((v) => v.name?.includes('Rookie') || v?.name?.includes('Bronze')).sort((a, b) => a.level - b.level);
        const next = vipList?.filter((v) => v?.name?.includes('Silver')).sort((a, b) => a.level - b.level);
        const subLevels = getSubLevels(rangeVipList, next);
        return {
            level: {
                id: 'Bronze',
                name: $t({id: "Bronze"}),
                progress: fillProgress(subLevels),
                isAvailable: true,
                isTotalComplete: subLevels.every(item => item.isComplete)
            },
            subLevels,
        };
    }, [level, exp]);

    const silverData = useMemo(() => {
        const rangeVipList = vipList?.filter((v) => v?.name?.includes('Silver')).sort((a, b) => a.level - b.level);
        const next = vipList?.filter((v) => v?.name?.includes('Gold')).sort((a, b) => a.level - b.level);
        const subLevels = getSubLevels(rangeVipList, next);

        return {
            level: {
                id: 'Silver',
                name: $t({id: "Silver"}),
                progress: fillProgress(subLevels),
                isAvailable: true,
                isTotalComplete: subLevels.every(item => item.isComplete)
            },
            subLevels,
        };
    }, [level, exp]);

    const goldData = useMemo(() => {
        const rangeVipList = vipList?.filter((v) => v?.name?.includes('Gold')).sort((a, b) => a.level - b.level);
        const next = vipList?.filter((v) => v?.name?.includes('Platinum')).sort((a, b) => a.level - b.level);
        const subLevels = getSubLevels(rangeVipList, next);

        return {
            level: {
                id: 'Gold',
                name: $t({id: "Gold"}),
                progress: fillProgress(subLevels),
                isAvailable: true,
                isTotalComplete: subLevels.every(item => item.isComplete)
            },
            subLevels,
        };
    }, [level, exp]);

    const platinumData = useMemo(() => {
        const rangeVipList = vipList?.filter((v) => v?.name?.includes('Platinum')).sort((a, b) => a.level - b.level);
        const next = vipList?.filter((v) => v?.name?.includes('Pearl')).sort((a, b) => a.level - b.level);
        const subLevels = getSubLevels(rangeVipList, next);

        return {
            level: {
                id: 'Platinum',
                name: $t({id: "Platinum"}),
                progress: fillProgress(subLevels),
                isAvailable: true,
                isTotalComplete: subLevels.every(item => item.isComplete)
            },
            subLevels,
        };
    }, [level, exp]);

    const pearlData = useMemo(() => {
        const rangeVipList = vipList?.filter((v) => v?.name?.includes('Pearl')).sort((a, b) => a.level - b.level);
        const next = vipList?.filter((v) => v?.name?.includes('Onyx')).sort((a, b) => a.level - b.level);
        const subLevels = getSubLevels(rangeVipList, next);

        return {
            level: {
                id: 'Pearl',
                name: $t({id: "Pearl"}),
                progress: fillProgress(subLevels),
                isAvailable: true,
                isTotalComplete: subLevels.every(item => item.isComplete)
            },
            subLevels,
        };
    }, [level, exp]);

    const onyxData = useMemo(() => {
        const rangeVipList = vipList?.filter((v) => v?.name?.includes('Onyx')).sort((a, b) => a.level - b.level);
        const next = vipList?.filter((v) => v?.name?.includes('Diamond')).sort((a, b) => a.level - b.level);
        const subLevels = getSubLevels(rangeVipList, next);

        return {
            level: {
                id: 'Onyx',
                name: $t({id: "Onyx"}),
                progress: fillProgress(subLevels),
                isAvailable: true,
                isTotalComplete: subLevels.every(item => item.isComplete)
            },
            subLevels,
        };
    }, [level, exp]);

    const diamondData = useMemo(() => {
        const rangeVipList = vipList?.filter((v) => v?.name?.includes('Diamond')).sort((a, b) => a.level - b.level);
        const next = vipList?.filter((v) => v?.name?.includes('Palladium')).sort((a, b) => a.level - b.level);
        const subLevels = getSubLevels(rangeVipList, next);

        return {
            level: {
                id: 'Diamond',
                name: $t({id: "Diamond"}),
                progress: fillProgress(subLevels),
                isAvailable: true,
                isTotalComplete: subLevels.every(item => item.isComplete)
            },
            subLevels,
        };
    }, [level, exp]);

    const palladiumData = useMemo(() => {
        const rangeVipList = vipList?.filter((v) => v?.name?.includes('Palladium')).sort((a, b) => a.level - b.level);
        const next = vipList?.filter((v) => v?.name?.includes("Castle")).sort((a, b) => a.level - b.level);
        const subLevels = getSubLevels(rangeVipList, next);

        return {
            level: {
                id: 'Palladium',
                name: $t({id: "Palladium"}),
                progress: fillProgress(subLevels),
                isAvailable: false,
                isTotalComplete: subLevels.every(item => item.isComplete)
            },
            subLevels,
        };
    }, [level, exp]);

    const castleData = useMemo(() => {
        const rangeVipList = vipList?.filter((v) => v?.name?.includes('Castle')
    ).sort((a, b) => a.level - b.level);
        const subLevels = getSubLevels(rangeVipList, []);

        return {
            level: {
                id: 'Castle',
                name: $t({id: "Castle"}),
                progress: fillProgress(subLevels),
                isAvailable: false,
                isTotalComplete: subLevels.every(item => item.isComplete)
            },
            subLevels,
        };
    }, [level, exp]);

    return {
        bronzeData,
        silverData,
        goldData,
        platinumData,
        pearlData,
        onyxData,
        diamondData,
        palladiumData,
        castleData
    }
}
